import { useState, useMemo, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';

interface RegistoAlteracao {
  id: string;
  entidade?: string;
  funcionarioId: string;
  funcionarioNome: string;
  tipoAcao: 'criacao' | 'edicao' | 'eliminacao';
  campoAlterado: string | null;
  valorAnterior: string | null;
  valorNovo: string | null;
  descricao: string;
  dataAlteracao: string;
  modulo?: string | null;
  userEmail?: string | null;
}

function toRegisto(row: Record<string, unknown>): RegistoAlteracao {
  return {
    id: row.id as string,
    entidade: (row.entidade as string) || 'funcionario',
    funcionarioId: (row.entidade_id as string) || (row.funcionario_id as string) || '',
    funcionarioNome: (row.entidade_nome as string) || (row.funcionario_nome as string) || '—',
    tipoAcao: (row.tipo_acao as RegistoAlteracao['tipoAcao']),
    campoAlterado: (row.campo_alterado as string | null),
    valorAnterior: (row.valor_anterior as string | null),
    valorNovo: (row.valor_novo as string | null),
    descricao: (row.descricao as string),
    dataAlteracao: (row.data_alteracao as string),
    modulo: (row.modulo as string | null) ?? null,
    userEmail: (row.user_email as string | null) ?? null,
  };
}

const tipoConfig = {
  criacao: { label: 'Criação', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  edicao: { label: 'Edição', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  eliminacao: { label: 'Eliminação', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
};

const avatarColors = [
  'bg-rose-100 text-rose-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-teal-100 text-teal-700',
  'bg-sky-100 text-sky-700',
  'bg-orange-100 text-orange-700',
];

const ENTIDADE_ICON: Record<string, string> = {
  funcionario: 'ri-user-line',
  formacao: 'ri-book-open-line',
  participante: 'ri-user-follow-line',
  documento: 'ri-file-text-line',
  formador: 'ri-user-star-line',
  utilizador: 'ri-team-line',
  unidade: 'ri-building-2-line',
  carreira: 'ri-briefcase-line',
};

const PAGE_SIZE = 20;

function getInitials(nome: string) {
  const parts = nome.split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}

function toISODate(d: Date): string {
  return d.toISOString().split('T')[0];
}

export default function AlteracoesTab() {
  const { activeMunicipio } = useActiveMunicipio();
  const [registos, setRegistos] = useState<RegistoAlteracao[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [entidadeFilter, setEntidadeFilter] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const municipioId = activeMunicipio?.id ?? '00000000-0000-0000-0000-000000000001';

  const loadRegistos = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('historico_alteracoes')
      .select('*')
      .eq('municipio_id', municipioId)
      .order('data_alteracao', { ascending: false });
    setRegistos((data ?? []).map(toRegisto));
    setLoading(false);
  }, [municipioId]);

  useEffect(() => { loadRegistos(); }, [loadRegistos]);

  // Reset página quando filtros mudam
  useEffect(() => { setPage(1); }, [search, tipoFilter, entidadeFilter, dataInicio, dataFim]);

  const filtered = useMemo(() => registos.filter((r) => {
    const matchSearch = search === '' ||
      r.funcionarioNome.toLowerCase().includes(search.toLowerCase()) ||
      r.funcionarioId.includes(search) ||
      r.descricao.toLowerCase().includes(search.toLowerCase());
    const matchTipo = tipoFilter === '' || r.tipoAcao === tipoFilter;
    const matchEntidade = entidadeFilter === '' || (r.entidade || 'funcionario') === entidadeFilter;
    const rDate = r.dataAlteracao.split('T')[0];
    const matchInicio = dataInicio === '' || rDate >= dataInicio;
    const matchFim = dataFim === '' || rDate <= dataFim;
    return matchSearch && matchTipo && matchEntidade && matchInicio && matchFim;
  }), [registos, search, tipoFilter, entidadeFilter, dataInicio, dataFim]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalCriacoes = registos.filter((r) => r.tipoAcao === 'criacao').length;
  const totalEdicoes = registos.filter((r) => r.tipoAcao === 'edicao').length;
  const totalEliminacoes = registos.filter((r) => r.tipoAcao === 'eliminacao').length;

  const hasFilters = search || tipoFilter || entidadeFilter || dataInicio || dataFim;

  function clearFilters() {
    setSearch(''); setTipoFilter(''); setEntidadeFilter(''); setDataInicio(''); setDataFim('');
  }

  function exportCSV() {
    const headers = ['Data', 'Funcionário', 'Nº', 'Tipo', 'Descrição', 'Campo Alterado', 'Valor Anterior', 'Novo Valor'];
    const rows = filtered.map((r) => [
      formatDate(r.dataAlteracao),
      r.funcionarioNome,
      r.funcionarioId,
      tipoConfig[r.tipoAcao].label,
      r.descricao,
      r.campoAlterado ?? '',
      r.valorAnterior ?? '',
      r.valorNovo ?? '',
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const today = toISODate(new Date());
    a.download = `alteracoes_${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-5">

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: 'ri-history-line', value: registos.length, label: 'Total de Registos', iconBg: 'bg-slate-100 text-slate-600', accent: 'border-l-slate-400' },
          { icon: 'ri-user-add-line', value: totalCriacoes, label: 'Criações', iconBg: 'bg-emerald-50 text-emerald-600', accent: 'border-l-emerald-400' },
          { icon: 'ri-edit-line', value: totalEdicoes, label: 'Edições', iconBg: 'bg-amber-50 text-amber-600', accent: 'border-l-amber-400' },
          { icon: 'ri-delete-bin-line', value: totalEliminacoes, label: 'Eliminações', iconBg: 'bg-red-50 text-red-500', accent: 'border-l-red-400' },
        ].map((s) => (
          <div key={s.label} className={`bg-white rounded-xl border border-slate-100 border-l-4 ${s.accent} p-4 flex items-center gap-3`}>
            <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${s.iconBg}`}>
              <i className={`${s.icon} text-lg`} />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-800 leading-tight">{s.value}</p>
              <p className="text-xs text-slate-500 leading-tight">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-slate-100 p-4 space-y-3">
        {/* Linha 1: pesquisa + tipo + atualizar + exportar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <div className="w-9 h-9 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
              <i className="ri-search-line text-base" />
            </div>
            <input
              type="text"
              placeholder="Pesquisar por nome, nº ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
          </div>
          <select
            value={tipoFilter}
            onChange={(e) => setTipoFilter(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 cursor-pointer"
          >
            <option value="">Todos os Tipos</option>
            <option value="criacao">Criação</option>
            <option value="edicao">Edição</option>
            <option value="eliminacao">Eliminação</option>
          </select>
          <select
            value={entidadeFilter}
            onChange={(e) => setEntidadeFilter(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 cursor-pointer"
          >
            <option value="">Todas as Entidades</option>
            <option value="funcionario">Funcionário</option>
            <option value="formacao">Formação</option>
            <option value="participante">Participante</option>
            <option value="documento">Documento</option>
            <option value="formador">Formador</option>
            <option value="utilizador">Utilizador</option>
            <option value="unidade">Unidade</option>
            <option value="carreira">Carreira</option>
          </select>
          <button
            onClick={loadRegistos}
            className="whitespace-nowrap flex items-center gap-2 border border-slate-200 text-slate-600 text-sm px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line text-sm" /></div>
            Atualizar
          </button>
          <button
            onClick={exportCSV}
            disabled={filtered.length === 0}
            className="whitespace-nowrap flex items-center gap-2 bg-slate-900 text-white text-sm px-3 py-2.5 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-40"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-download-2-line text-sm" /></div>
            Exportar CSV
          </button>
        </div>

        {/* Linha 2: filtro por datas */}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-5 h-5 flex items-center justify-center text-slate-400">
              <i className="ri-calendar-line text-sm" />
            </div>
            <span className="text-xs font-medium text-slate-500">Intervalo de datas:</span>
          </div>
          <div className="flex items-center gap-2 flex-1 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-400 whitespace-nowrap">De</label>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 cursor-pointer"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-400 whitespace-nowrap">Até</label>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                min={dataInicio || undefined}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 cursor-pointer"
              />
            </div>
            {(dataInicio || dataFim) && (
              <button
                onClick={() => { setDataInicio(''); setDataFim(''); }}
                className="whitespace-nowrap flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
              >
                <i className="ri-close-circle-line text-sm" />
                Limpar datas
              </button>
            )}
          </div>
        </div>

        {/* Linha 3: resumo filtros */}
        <div className="flex items-center justify-between pt-1 border-t border-slate-100">
          <p className="text-xs text-slate-400">
            A mostrar <span className="font-semibold text-slate-600">{filtered.length}</span> de {registos.length} registo(s)
            {filtered.length !== registos.length && (
              <span className="text-slate-300"> · página {page}/{totalPages}</span>
            )}
          </p>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="whitespace-nowrap flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
            >
              <i className="ri-close-circle-line text-sm" />
              Limpar todos os filtros
            </button>
          )}
        </div>
      </div>

      {/* Tabela */}
      {loading ? (
        <div className="bg-white rounded-xl border border-slate-100 py-16 flex flex-col items-center gap-3 text-slate-400">
          <div className="w-8 h-8 flex items-center justify-center"><i className="ri-loader-4-line text-3xl animate-spin" /></div>
          <p className="text-sm">A carregar histórico...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 py-16 flex flex-col items-center gap-3 text-slate-400">
          <div className="w-12 h-12 flex items-center justify-center"><i className="ri-history-line text-4xl" /></div>
          <p className="text-sm">{hasFilters ? 'Nenhum registo encontrado com os filtros aplicados.' : 'Nenhum registo disponível.'}</p>
          {hasFilters && (
            <button onClick={clearFilters} className="whitespace-nowrap text-xs text-slate-500 hover:text-slate-800 underline cursor-pointer">Limpar filtros</button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide w-8"></th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Funcionário</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Tipo</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Descrição</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Data</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((r) => {
                  const cfg = tipoConfig[r.tipoAcao];
                  const colorIdx = parseInt(r.funcionarioId.replace(/\D/g, ''), 10) % avatarColors.length;
                  const colorClass = avatarColors[isNaN(colorIdx) ? 0 : colorIdx];
                  const isExpanded = expandedId === r.id;
                  const hasDetail = r.campoAlterado || r.valorAnterior || r.valorNovo;

                  return (
                    <>
                      <tr
                        key={r.id}
                        className={`border-b border-slate-50 transition-colors ${isExpanded ? 'bg-slate-50/80' : 'hover:bg-slate-50/50'}`}
                      >
                        <td className="px-3 py-3.5">
                          {hasDetail ? (
                            <button
                              onClick={() => setExpandedId(isExpanded ? null : r.id)}
                              className="w-6 h-6 flex items-center justify-center rounded-md bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors cursor-pointer"
                            >
                              <i className={`ri-${isExpanded ? 'subtract' : 'add'}-line text-xs`} />
                            </button>
                          ) : (
                            <div className="w-6 h-6" />
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${colorClass}`}>
                              {getInitials(r.funcionarioNome)}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-800">{r.funcionarioNome}</p>
                              <p className="text-xs text-slate-400">#{r.funcionarioId}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 max-w-xs">
                          <p className="text-sm text-slate-600 truncate">{r.descricao}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-sm text-slate-500 whitespace-nowrap">{formatDate(r.dataAlteracao)}</p>
                        </td>
                      </tr>
                      {isExpanded && hasDetail && (
                        <tr key={`${r.id}-detail`} className="border-b border-slate-100 bg-slate-50/80">
                          <td colSpan={5} className="px-5 pb-4 pt-0">
                            <div className="ml-14 bg-white border border-slate-100 rounded-xl px-5 py-4 flex flex-wrap gap-6">
                              {r.campoAlterado && (
                                <div>
                                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Campo Alterado</p>
                                  <p className="text-sm font-medium text-slate-700">{r.campoAlterado}</p>
                                </div>
                              )}
                              {r.valorAnterior && (
                                <div>
                                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Valor Anterior</p>
                                  <span className="inline-block text-sm bg-red-50 text-red-700 px-2 py-0.5 rounded">{r.valorAnterior}</span>
                                </div>
                              )}
                              {r.valorNovo && (
                                <div>
                                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Novo Valor</p>
                                  <span className="inline-block text-sm bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">{r.valorNovo}</span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="px-5 py-3.5 border-t border-slate-100 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-slate-400">
                Página <span className="font-semibold text-slate-600">{page}</span> de <span className="font-semibold text-slate-600">{totalPages}</span>
                <span className="text-slate-300"> · {filtered.length} registos</span>
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-skip-back-line text-sm" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-sm" />
                </button>

                {/* Números de página */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                    if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) {
                      acc.push('...');
                    }
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '...' ? (
                      <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-xs text-slate-400">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p as number)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                          page === p ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-sm" />
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-skip-forward-line text-sm" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
